import { useState, useEffect, useMemo } from 'react';
import { useAllDevices, useAssignPartner } from '../../../hooks/admin/useDevices';
import { useUsers } from '../../../hooks/admin/useUsers';
import { useGroups } from '../../../hooks/admin/useGroups';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { useTableUrlState } from '../../../hooks/useTableUrlState';
import { useColumnVisibility } from '../../../hooks/useColumnVisibility';
import DeviceTable, { DEVICE_COLUMNS, DEVICE_COLUMNS_DEFAULT_HIDDEN } from '../../../components/admin/DeviceTable';
import Pagination from '../../../components/admin/Pagination';
import SearchInput from '../../../components/admin/SearchInput';
import ColumnVisibilityMenu from '../../../components/admin/ColumnVisibilityMenu';
import type { Device } from '../../../types/admin';

type SortField = 'DEVICE_ID' | 'SERIAL_NUMBER' | 'DATE_CODE' | 'HW_VERSION' | 'WISOL_ID' | 'LOCATION_ID' | 'GROUP';

function compareDevices(a: Device, b: Device, sortBy: SortField): number {
  switch (sortBy) {
    case 'DEVICE_ID':
      return a.deviceId - b.deviceId;
    case 'SERIAL_NUMBER':
      return Number(a.serialNumber) - Number(b.serialNumber);
    case 'DATE_CODE':
      return a.dateCode.localeCompare(b.dateCode);
    case 'HW_VERSION':
      return a.hardwareVersion.localeCompare(b.hardwareVersion, undefined, { numeric: true });
    case 'WISOL_ID':
      return a.sigfoxId.localeCompare(b.sigfoxId);
    case 'LOCATION_ID':
      return a.locationId - b.locationId;
    case 'GROUP':
      return (a.locationName ?? '').localeCompare(b.locationName ?? '');
  }
}

export default function DeviceList() {
  const { page, pageSize, search, setPage, setPageSize, setSearch, getFilter, setFilter } = useTableUrlState();
  const groupFilter = getFilter('group');
  const partnerFilter = getFilter('partner');
  const [sortBy, setSortBy] = useState<SortField>('SERIAL_NUMBER');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [pendingPartnerId, setPendingPartnerId] = useState<string>('');
  const [confirmUnassignOpen, setConfirmUnassignOpen] = useState(false);

  const columnVisibility = useColumnVisibility({
    storageKey: 'admin.devices.hiddenColumns',
    allColumnKeys: DEVICE_COLUMNS.map((c) => c.key),
    defaultHidden: DEVICE_COLUMNS_DEFAULT_HIDDEN,
  });

  const { data: users } = useUsers();
  const { data: groups } = useGroups();
  const sortedGroups = useMemo(
    () => [...(groups ?? [])].sort((a, b) => a.locationName.localeCompare(b.locationName)),
    [groups],
  );
  const partners = useMemo(
    () => (users ?? []).filter((u) => u.role === 'partner'),
    [users],
  );
  const assignPartner = useAssignPartner();

  const debouncedSearch = useDebouncedValue(search, 200);

  const { data: allDevices, isLoading, isFetching, error } = useAllDevices();

  const filteredDevices = useMemo(() => {
    const list = allDevices ?? [];
    const q = debouncedSearch.trim().toLowerCase();
    const groupId = groupFilter === '' ? null : Number(groupFilter);
    return list.filter((d) => {
      if (groupId !== null && d.locationId !== groupId) return false;
      if (partnerFilter === 'none') {
        if (d.partnerUserId != null) return false;
      } else if (partnerFilter !== '') {
        if (String(d.partnerUserId ?? '') !== partnerFilter) return false;
      }
      if (!q) return true;
      return (
        d.serialNumber.toLowerCase().includes(q) ||
        (d.sigfoxId ?? '').toLowerCase().includes(q) ||
        (d.sigfoxPac ?? '').toLowerCase().includes(q)
      );
    });
  }, [allDevices, debouncedSearch, groupFilter, partnerFilter]);

  const sortedDevices = useMemo(() => {
    const sorted = [...filteredDevices].sort((a, b) => compareDevices(a, b, sortBy));
    return sortDir === 'asc' ? sorted : sorted.reverse();
  }, [filteredDevices, sortBy, sortDir]);

  const totalCount = sortedDevices.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  useEffect(() => {
    if (!allDevices) return;
    if (page > totalPages) setPage(totalPages);
  }, [allDevices, page, totalPages, setPage]);

  const pagedDevices = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedDevices.slice(start, start + pageSize);
  }, [sortedDevices, page, pageSize]);

  const performAssignment = async () => {
    const partnerUserId =
      pendingPartnerId === '' ? null : Number(pendingPartnerId);
    try {
      await assignPartner.mutateAsync({ deviceIds: selectedIds, partnerUserId });
      setSelectedIds([]);
      setPendingPartnerId('');
      setConfirmUnassignOpen(false);
    } catch {
      // toast emitted by hook
    }
  };

  const handleApplyAssignment = () => {
    if (selectedIds.length === 0) return;
    if (pendingPartnerId === '') {
      setConfirmUnassignOpen(true);
      return;
    }
    void performAssignment();
  };

  const handleSort = (field: SortField) => {
    setPage(1);
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  if (isLoading && !allDevices) return <div>Loading devices...</div>;
  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return <div>Error loading devices: {errorMessage}</div>;
  }

  return (
    <div className="flex flex-col">
      <div className="bg-white shadow-sm border-b p-4 space-y-4">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Remote IoAg Controllers</h1>
        </div>

        <div className="flex items-center gap-4">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search by serial number, Sigfox ID, or PAC..."
          />
          <select
            value={groupFilter}
            onChange={(e) => setFilter('group', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            aria-label="Filter by group"
          >
            <option value="">All groups</option>
            {sortedGroups.map((g) => (
              <option key={g.locationId} value={String(g.locationId)}>
                {g.locationName}
              </option>
            ))}
          </select>
          <select
            value={partnerFilter}
            onChange={(e) => setFilter('partner', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            aria-label="Filter by partner"
          >
            <option value="">All partners</option>
            <option value="none">— No partner —</option>
            {partners.map((p) => {
              const label =
                [p.firstName, p.lastName].filter(Boolean).join(' ').trim()
                || p.accountId
                || p.email;
              return (
                <option key={p.userId} value={String(p.userId)}>
                  {label}
                </option>
              );
            })}
          </select>
          <ColumnVisibilityMenu
            columns={DEVICE_COLUMNS}
            hidden={columnVisibility.hidden}
            onToggle={columnVisibility.toggle}
            onReset={columnVisibility.resetToDefaults}
          />
          <div className="flex items-center gap-3">
            {isFetching && (
              <div className="flex items-center space-x-2 text-blue-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm">Loading...</span>
              </div>
            )}
            <div className="text-gray-600">
              {totalCount} device{totalCount === 1 ? '' : 's'}
            </div>
          </div>
        </div>

        {(() => {
          const hasSelection = selectedIds.length > 0;
          const disabledClass = hasSelection ? '' : 'opacity-60 pointer-events-none';
          return (
            <div
              className={`flex flex-wrap items-center gap-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg ${disabledClass}`}
              aria-disabled={!hasSelection}
            >
              <span className="text-sm text-blue-900">
                <strong>{selectedIds.length}</strong> selected
              </span>
              <span className="text-sm text-blue-900">·</span>
              <label className="text-sm text-blue-900">Assign to partner:</label>
              <select
                value={pendingPartnerId}
                onChange={(e) => setPendingPartnerId(e.target.value)}
                disabled={!hasSelection}
                className="px-2 py-1 text-sm border border-gray-300 rounded disabled:bg-gray-100"
              >
                <option value="">— Unassign partner —</option>
                {partners.map((p) => {
                  const label =
                    [p.firstName, p.lastName].filter(Boolean).join(' ').trim()
                    || p.accountId
                    || p.email;
                  return (
                    <option key={p.userId} value={String(p.userId)}>
                      {label} ({p.email})
                    </option>
                  );
                })}
              </select>
              <button
                type="button"
                onClick={handleApplyAssignment}
                disabled={!hasSelection || assignPartner.isPending}
                className="px-3 py-1 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {assignPartner.isPending ? 'Applying…' : 'Apply'}
              </button>
              <button
                type="button"
                onClick={() => setSelectedIds([])}
                disabled={!hasSelection}
                className="px-3 py-1 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Clear selection
              </button>
            </div>
          );
        })()}
      </div>

      <div className="bg-white">
        {pagedDevices.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-gray-500">
              {search ? 'No devices match your search' : 'No devices found'}
            </p>
          </div>
        ) : (
          <DeviceTable
            devices={pagedDevices}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            hiddenColumns={columnVisibility.hidden}
          />
        )}
      </div>

      <div>
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          totalItems={totalCount}
        />
      </div>

      {confirmUnassignOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Unassign partner</h2>
            <p className="text-gray-600 mb-6">
              Remove the partner assignment from{' '}
              <strong>
                {selectedIds.length} device{selectedIds.length === 1 ? '' : 's'}
              </strong>
              ? This will leave the selected devices without a partner.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setConfirmUnassignOpen(false)}
                disabled={assignPartner.isPending}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => void performAssignment()}
                disabled={assignPartner.isPending}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg disabled:opacity-50"
              >
                {assignPartner.isPending ? 'Unassigning…' : 'Unassign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
